import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import { mergePathwayRoots } from '../utils/pathwayTaxonomy';
import './Taxonomy.css';

/**
 * User-facing labels (API / DB still use Course-Subject, Concentration, etc.).
 * "Core subject" = TK–12 subject areas (Math, ELA, Social Studies, etc.); avoids implying only Common Core standards.
 */
const TYPE_DISPLAY_NAME = {
  Standard: 'Standard',
  Skill: 'Skill',
  Objective: 'Objective',
  'Course-Subject': 'Core subject',
  Concentration: 'CTE pathway',
};

const getTypeDisplayName = (type) => TYPE_DISPLAY_NAME[type] || type;

/** Standards / skills / objectives can be scoped to CTE pathways and/or core subjects; pathway roots cannot. */
const PATHWAY_SCOPABLE_TYPES = ['Standard', 'Skill', 'Objective'];

const usesPathwayScope = (type) => PATHWAY_SCOPABLE_TYPES.includes(type);

const getCreateMaxSteps = (type) => {
  if (!type) return 4;
  if (type === 'Course-Subject' || type === 'Concentration') return 2;
  if (type === 'Standard') return 3;
  if (type === 'Skill' || type === 'Objective') return 4;
  return 4;
};

/** Align with TaxonomySelector CreateTaxonomyModal / pathway accents */
const TAX_TYPE_ACCENTS = {
  Concentration: {
    header: '#0369a1',
    softBg: '#e0f2fe',
    softBorder: '#7dd3fc',
    stroke: '#0ea5e9',
    selBg: '#e0f2fe',
    selBorder: '#7dd3fc',
  },
  Standard: {
    header: '#1d4ed8',
    softBg: '#eff6ff',
    softBorder: '#bfdbfe',
    stroke: '#3b82f6',
    selBg: '#eff6ff',
    selBorder: '#bfdbfe',
  },
  Skill: {
    header: '#047857',
    softBg: '#ecfdf5',
    softBorder: '#a7f3d0',
    stroke: '#10b981',
    selBg: '#ecfdf5',
    selBorder: '#a7f3d0',
  },
  Objective: {
    header: '#b45309',
    softBg: '#fffbeb',
    softBorder: '#fde68a',
    stroke: '#f59e0b',
    selBg: '#fffbeb',
    selBorder: '#fde68a',
  },
  'Course-Subject': {
    header: '#4338ca',
    softBg: '#eef2ff',
    softBorder: '#c7d2fe',
    stroke: '#6366f1',
    selBg: '#eef2ff',
    selBorder: '#c7d2fe',
  },
};

const getTypeAccent = (type) =>
  TAX_TYPE_ACCENTS[type] || {
    header: '#475569',
    softBg: '#f8fafc',
    softBorder: '#e2e8f0',
    stroke: '#94a3b8',
    selBg: '#f8fafc',
    selBorder: '#e2e8f0',
  };

const getTypeBadgeStyle = (type) => {
  const a = getTypeAccent(type);
  return {
    color: a.header,
    backgroundColor: a.selBg,
    border: `1px solid ${a.selBorder}`,
    fontWeight: 600,
  };
};

const TAXONOMY_CAPABILITY_DEFAULTS = {
  CreateConcentration: false,
  CreateCoreSubjects: false,
  EditConcentrations: false,
  EditCoreSubjects: false,
  EditStandardsSkillsObjectives: false,
  CreateStandardsSkillsObjectives: false,
};

const TX = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.42)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  panel: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 4px 24px rgba(15, 23, 42, 0.07)',
    padding: '18px 20px',
    maxWidth: '520px',
    width: 'min(92vw, 520px)',
    maxHeight: '82vh',
    overflow: 'auto',
  },
  title: { margin: 0, fontSize: '15px', fontWeight: 600, color: '#0f172a', letterSpacing: '-0.02em' },
  hint: { fontSize: '12px', color: '#64748b', lineHeight: 1.45 },
  label: { display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500, color: '#64748b' },
  field: {
    width: '100%',
    padding: '7px 10px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '13px',
    backgroundColor: '#fafafa',
    boxSizing: 'border-box',
  },
  btnSecondary: {
    padding: '7px 14px',
    fontSize: '13px',
    fontWeight: 500,
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#334155',
    cursor: 'pointer',
  },
  btnPrimary: {
    padding: '7px 14px',
    fontSize: '13px',
    fontWeight: 500,
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#334155',
    color: '#ffffff',
    cursor: 'pointer',
  },
  btnPrimaryDisabled: { backgroundColor: '#cbd5e1', color: '#f8fafc', cursor: 'not-allowed' },
};

/** Edit dialog — compact, same family as TX */
const EM = {
  panel: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 4px 24px rgba(15, 23, 42, 0.07)',
    padding: '20px 22px',
    maxWidth: '460px',
    width: 'min(92vw, 460px)',
    maxHeight: '86vh',
    overflow: 'auto',
  },
  section: { marginBottom: '14px' },
  scrollBox: {
    maxHeight: '168px',
    overflow: 'auto',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '6px 8px',
    backgroundColor: '#fafafa',
  },
  warn: {
    marginBottom: '10px',
    padding: '10px 12px',
    backgroundColor: '#fffbeb',
    borderRadius: '8px',
    border: '1px solid #fde68a',
    fontSize: '12px',
    color: '#92400e',
    lineHeight: 1.45,
  },
};

const Taxonomy = ({ userData }) => {
  const [taxonomyItems, setTaxonomyItems] = useState([]);
  const [concentrations, setConcentrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  /** null = all types; otherwise Standard | Skill | Objective | Course-Subject | Concentration */
  const [typeFilter, setTypeFilter] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  
  // Manager states
  const [selectedItem, setSelectedItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingParents, setEditingParents] = useState(false);
  const [editingConcentrations, setEditingConcentrations] = useState(false);
  const [taxonomyCapabilities, setTaxonomyCapabilities] = useState(TAXONOMY_CAPABILITY_DEFAULTS);
  
  // Form states
  const [newItem, setNewItem] = useState({
    title: '',
    description: '',
    short_title: '',
    type: '', // No default type selected
    concentration: '', // Changed to single selection
    parent: '' // Changed to single selection
  });
  const [editItem, setEditItem] = useState({});
  
  // Create flow: 1 type → 2 pathway | Course-Subject form | CTE pathway form → 3 title/desc → 4 parent (Skill/Objective)
  const [createStep, setCreateStep] = useState(1);
  const [parentSearchTerm, setParentSearchTerm] = useState(''); // Search term for parent selection
  /** Create wizard — scope step: CTE vs Core subject tabs (same as edit modal) */
  const [createPathwayScopeTab, setCreatePathwayScopeTab] = useState('cte');

  // Edit flow states
  const [editParentSearchTerm, setEditParentSearchTerm] = useState(''); // Search term for edit parent selection
  /** Edit modal: pathway scope list — CTE vs Core subject tabs */
  const [editPathwayScopeTab, setEditPathwayScopeTab] = useState('cte');

  useEffect(() => {
    fetchTaxonomyData();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/check-permission?pagePath=taxonomy', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) {
          setTaxonomyCapabilities({
            CreateConcentration: Boolean(res.data.capabilities?.CreateConcentration),
            CreateCoreSubjects: Boolean(res.data.capabilities?.CreateCoreSubjects),
            EditConcentrations: Boolean(res.data.capabilities?.EditConcentrations),
            EditCoreSubjects: Boolean(res.data.capabilities?.EditCoreSubjects),
            EditStandardsSkillsObjectives: Boolean(res.data.capabilities?.EditStandardsSkillsObjectives),
            CreateStandardsSkillsObjectives: Boolean(
              res.data.capabilities?.CreateStandardsSkillsObjectives
            ),
          });
        }
      } catch {
        if (!cancelled) {
          setTaxonomyCapabilities(TAXONOMY_CAPABILITY_DEFAULTS);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData?.account_id]);

  /** CTE pathway rows (Concentration type) + TK–12 core-subject roots (same idea as Assignment Builder `mergePathwayRoots`). */
  const pathwayRootOptions = useMemo(() => {
    const subjectRows = (taxonomyItems || [])
      .filter((t) => t.type === 'Course-Subject' && t.status !== 'deleted')
      .map((t) => ({ id: t.id, title: t.title || t.id }));
    return mergePathwayRoots(concentrations, subjectRows);
  }, [concentrations, taxonomyItems]);

  const pathwayRootsCte = useMemo(
    () => pathwayRootOptions.filter((r) => r.pathwayKind === 'concentration'),
    [pathwayRootOptions]
  );
  const pathwayRootsCore = useMemo(
    () => pathwayRootOptions.filter((r) => r.pathwayKind === 'course_subject'),
    [pathwayRootOptions]
  );

  const editPathwaySelectionCounts = useMemo(() => {
    const sel = editItem.selectedConcentrations || [];
    if (sel.length === 0) return { cte: 0, core: 0 };
    const cteSet = new Set(pathwayRootsCte.map((r) => r.id));
    const coreSet = new Set(pathwayRootsCore.map((r) => r.id));
    let cte = 0;
    let core = 0;
    for (const id of sel) {
      if (cteSet.has(id)) cte += 1;
      else if (coreSet.has(id)) core += 1;
    }
    return { cte, core };
  }, [editItem.selectedConcentrations, pathwayRootsCte, pathwayRootsCore]);

  const pathwayScopeLabel = (id) => {
    if (!id) return 'Not scoped';
    const r = pathwayRootOptions.find((p) => p.id === id);
    return r ? r.title : id;
  };

  const fetchTaxonomyData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/taxonomy/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTaxonomyItems(response.data.items);
      setConcentrations(response.data.concentrations);
    } catch (error) {
      console.error('Error fetching taxonomy data:', error);
      setError('Failed to load taxonomy data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleItemClick = (item) => {
    let canEdit = false;
    if (item.type === 'Concentration') canEdit = taxonomyCapabilities.EditConcentrations;
    else if (item.type === 'Course-Subject') canEdit = taxonomyCapabilities.EditCoreSubjects;
    else if (item.type === 'Standard' || item.type === 'Skill' || item.type === 'Objective') {
      canEdit = taxonomyCapabilities.EditStandardsSkillsObjectives;
    }
    if (!canEdit) {
      alert(`You do not have permission to edit ${getTypeDisplayName(item.type)} items.`);
      return;
    }
    setSelectedItem(item);
    setEditItem({
      ...item,
      selectedConcentrations: usesPathwayScope(item.type) ? item.concentrations || [] : [],
      selectedParents: item.parents || [],
    });
    setEditParentSearchTerm('');
    setEditPathwayScopeTab('cte');
    setShowEditModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateStep(1);
    setParentSearchTerm('');
    setCreatePathwayScopeTab('cte');
    setNewItem({
      title: '',
      description: '',
      short_title: '',
      type: '',
      concentration: '',
      parent: '',
    });
  };

  const handleCreateNew = () => {
    if (!canCreateAnyTaxonomy) {
      alert('You do not have permission to create taxonomy items.');
      return;
    }
    setNewItem({
      title: '',
      description: '',
      short_title: '',
      type: '',
      concentration: '',
      parent: '',
    });
    setCreateStep(1);
    setParentSearchTerm('');
    setCreatePathwayScopeTab('cte');
    setShowCreateModal(true);
  };

  const handleSaveEdit = async () => {
    if (
      (editItem.type === 'Concentration' && !taxonomyCapabilities.EditConcentrations) ||
      (editItem.type === 'Course-Subject' && !taxonomyCapabilities.EditCoreSubjects) ||
      ((editItem.type === 'Standard' || editItem.type === 'Skill' || editItem.type === 'Objective') &&
        !taxonomyCapabilities.EditStandardsSkillsObjectives)
    ) {
      alert(`You do not have permission to edit ${getTypeDisplayName(editItem.type)} items.`);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/taxonomy/${editItem.id}`, {
        title: editItem.title,
        description: editItem.description,
        short_title: editItem.short_title != null ? String(editItem.short_title).trim() : '',
        concentrations: usesPathwayScope(editItem.type) ? editItem.selectedConcentrations || [] : [],
        parents: editItem.selectedParents || [],
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await fetchTaxonomyData();
      setShowEditModal(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Error updating taxonomy item:', error);
      alert('Failed to update item. Please try again.');
    }
  };

  const handleCreateItem = async (overrides = {}) => {
    const payload = { ...newItem, ...overrides };
    const titleTrim = String(payload.title || '').trim();
    if (!payload.type) return;
    if (
      (payload.type === 'Concentration' && !taxonomyCapabilities.CreateConcentration) ||
      (payload.type === 'Course-Subject' && !taxonomyCapabilities.CreateCoreSubjects) ||
      ((payload.type === 'Standard' || payload.type === 'Skill' || payload.type === 'Objective') &&
        !taxonomyCapabilities.CreateStandardsSkillsObjectives)
    ) {
      alert(`You do not have permission to create ${getTypeDisplayName(payload.type)} items.`);
      return;
    }
    if (!titleTrim) {
      alert('Please enter a title.');
      return;
    }
    if (usesPathwayScope(payload.type) && !payload.concentration) {
      alert('Please select a CTE pathway or core subject scope.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const shortTrim =
        payload.short_title != null && String(payload.short_title).trim() !== ''
          ? String(payload.short_title).trim()
          : '';
      await axios.post(
        '/api/taxonomy',
        {
          title: titleTrim,
          description: payload.description || null,
          short_title: shortTrim,
          type: payload.type,
          concentrations: payload.concentration ? [payload.concentration] : [],
          parents: payload.parent ? [payload.parent] : [],
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      await fetchTaxonomyData();
      closeCreateModal();
    } catch (error) {
      console.error('Error creating taxonomy item:', error);
      alert('Failed to create item. Please try again.');
    }
  };

  // Helper function to get applicable parents based on type and hierarchy rules
  const getApplicableParents = (itemType, currentItemId = null, searchTerm = '') => {
    if (itemType === 'Course-Subject' || itemType === 'Concentration') {
      return [];
    }

    let applicable = taxonomyItems.filter(item => item.id !== currentItemId);
    
    if (itemType === 'Skill') {
      // Skills can only be children to Standards or Objectives
      applicable = applicable.filter(item => item.type === 'Standard' || item.type === 'Objective');
    } else if (itemType === 'Objective') {
      // Objectives can only be children to Standards or Objectives
      applicable = applicable.filter(item => item.type === 'Standard' || item.type === 'Objective');
    } else if (itemType === 'Standard') {
      // Standards cannot have parents
      return [];
    }
    
    // Filter out items that would create more than 2 levels
    applicable = applicable.filter(item => {
      // If the potential parent has no parents, it's level 1, so this would be level 2 (max allowed)
      if (!item.parents || item.parents.length === 0) {
        return true;
      }
      // If the potential parent has parents, this would be level 3 (not allowed)
      return false;
    });
    
    // Apply search filter if search term is provided
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      applicable = applicable.filter(item =>
        (item.title && item.title.toLowerCase().includes(term)) ||
        (item.description && item.description.toLowerCase().includes(term)) ||
        (item.type && item.type.toLowerCase().includes(term))
      );
    }
    
    return applicable;
  };

  const getFilteredItems = () => {
    let filtered = taxonomyItems;

    if (typeFilter) {
      filtered = filtered.filter((item) => item.type === typeFilter);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        (item.title && item.title.toLowerCase().includes(term)) ||
        (item.description && item.description.toLowerCase().includes(term)) ||
        (item.concentration_names && item.concentration_names.some(name => name.toLowerCase().includes(term))) ||
        (item.parent_names && item.parent_names.some(name => name.toLowerCase().includes(term)))
      );
    }

    return filtered;
  };

  const getChildren = (itemId) => {
    return taxonomyItems.filter(item => 
      Array.isArray(item.parents) && item.parents.includes(itemId)
    );
  };

  const getRootItems = () => {
    return taxonomyItems.filter(item => 
      !Array.isArray(item.parents) || item.parents.length === 0
    );
  };

  const renderTaxonomyItem = (item, level = 0) => {
    const children = getChildren(item.id);
    const hasChildren = children.length > 0;
    const isExpanded = expandedItems.has(item.id);
    const typeKey =
      item.type && ['Standard', 'Skill', 'Objective', 'Course-Subject', 'Concentration'].includes(item.type)
        ? item.type
        : '__default';

    return (
      <div key={item.id} className={level > 0 ? 'tax-node tax-node--child' : 'tax-node'}>
        <div
          className="tax-item"
          data-tax-type={typeKey}
          onClick={() => handleItemClick(item)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleItemClick(item);
            }
          }}
        >
          <div className="tax-item__row">
            <div className="tax-item__main">
              <div className="tax-item__title-row">
                {hasChildren && (
                  <button
                    type="button"
                    className="tax-item__expand"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpanded(item.id);
                    }}
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? 'Collapse nested items' : 'Expand nested items'}
                  >
                    {isExpanded ? '▼' : '▶'}
                  </button>
                )}
                <span className="tax-type-badge" data-tax-type={typeKey}>
                  {getTypeDisplayName(item.type)}
                </span>
                <h4 className="tax-item__title">
                  {item.title}
                  {item.short_title ? (
                    <span style={{ fontWeight: 500, color: '#64748b', marginLeft: '8px' }}>
                      ({item.short_title})
                    </span>
                  ) : null}
                </h4>
              </div>

              {item.description && (
                <p className="tax-item__desc">{item.description}</p>
              )}

              <div className="tax-item__meta">
                {item.concentration_names && item.concentration_names.length > 0 && (
                  <div className="tax-chip">
                    <span className="tax-chip__label">Pathways</span>
                    {item.concentration_names.map((name, index) => (
                      <span key={index} className="tax-chip__pill">
                        {name}
                      </span>
                    ))}
                  </div>
                )}

                {item.parent_names && item.parent_names.length > 0 && (
                  <div className="tax-chip">
                    <span className="tax-chip__label">Parent</span>
                    {item.parent_names.map((name, index) => {
                      const warn =
                        name.includes('Missing Parent') ||
                        name.includes('Untitled Parent') ||
                        name.includes('...');
                      return (
                        <span
                          key={index}
                          className={`tax-chip__pill${warn ? ' tax-chip__pill--warn' : ''}`}
                          title={name}
                        >
                          {name}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div>{children.map((child) => renderTaxonomyItem(child, level + 1))}</div>
        )}
      </div>
    );
  };

  const getTypeColor = (type) => getTypeAccent(type).header;
  const canCreateAnyTaxonomy =
    taxonomyCapabilities.CreateConcentration ||
    taxonomyCapabilities.CreateCoreSubjects ||
    taxonomyCapabilities.CreateStandardsSkillsObjectives;

  const filteredItems = getFilteredItems();
  const shouldShowHierarchy = !searchTerm.trim() && !typeFilter;
  const displayItems = shouldShowHierarchy
    ? getRootItems().filter((item) => filteredItems.some((filtered) => filtered.id === item.id))
    : filteredItems;

  if (loading) {
    return (
      <div className="tax-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '320px' }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="tax-page">
        <div className="tax-empty" style={{ borderStyle: 'solid', borderColor: '#fecaca', background: '#fef2f2', color: '#991b1b' }}>
          <strong style={{ display: 'block', marginBottom: '8px' }}>Could not load taxonomy</strong>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="tax-page">
      <div className="tax-page__header">
        <div>
          <h1 className="tax-page__title">Taxonomy</h1>
          <p className="tax-page__lead">
            Browse the hierarchy, or search. Click a row to edit pathways, parents, and details.
          </p>
        </div>
        <button
          type="button"
          className="tax-page__create"
          onClick={handleCreateNew}
          title="Create taxonomy item"
          disabled={!canCreateAnyTaxonomy}
          style={!canCreateAnyTaxonomy ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
        >
          +
        </button>
      </div>

      <div className="tax-toolbar">
        <div className="tax-search-wrap">
          <label className="tax-search-label" htmlFor="tax-search-input">
            Search
          </label>
          <input
            id="tax-search-input"
            type="search"
            className="tax-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Title, description, pathway, or parent…"
            autoComplete="off"
          />
        </div>
        <div className="tax-toolbar-meta">
          {filteredItems.length} of {taxonomyItems.length}
          {shouldShowHierarchy ? ' · hierarchy' : typeFilter ? ' · type filter' : searchTerm.trim() ? ' · search' : ''}
        </div>
      </div>

      <div className="tax-legend" role="group" aria-label="Filter by type">
        <span className="tax-legend__label">Types</span>
        <div className="tax-legend__items">
          <button
            type="button"
            className={`tax-legend__chip${typeFilter === null ? ' tax-legend__chip--active' : ''}`}
            onClick={() => setTypeFilter(null)}
          >
            All
          </button>
          {[
            { type: 'Concentration', label: 'CTE pathway', swatch: '#0ea5e9' },
            { type: 'Standard', label: 'Standard', swatch: '#3b82f6' },
            { type: 'Skill', label: 'Skill', swatch: '#10b981' },
            { type: 'Objective', label: 'Objective', swatch: '#f59e0b' },
            { type: 'Course-Subject', label: 'Core subject', swatch: '#6366f1' },
          ].map(({ type, label, swatch }) => (
            <button
              key={type}
              type="button"
              className={`tax-legend__chip${typeFilter === type ? ' tax-legend__chip--active' : ''}`}
              onClick={() => setTypeFilter((prev) => (prev === type ? null : type))}
              title={typeFilter === type ? 'Show all types' : `Show only ${label}`}
            >
              <span className="tax-legend__swatch" style={{ background: swatch }} />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="tax-tree">
        <h2 className="tax-section-title">
          {shouldShowHierarchy ? 'Hierarchy' : typeFilter ? 'Filtered' : searchTerm.trim() ? 'Search results' : 'Items'}
        </h2>

        {displayItems.length === 0 ? (
          <div className="tax-empty">
            {searchTerm.trim()
              ? 'No items match your search.'
              : typeFilter
                ? 'No items of this type.'
                : 'No taxonomy items yet. Use + to create one.'}
          </div>
        ) : (
          <div>
            {shouldShowHierarchy
              ? displayItems.map((item) => renderTaxonomyItem(item))
              : displayItems.map((item) => renderTaxonomyItem(item, 0))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedItem && (
        <div style={TX.overlay}>
          <div style={EM.panel} role="dialog" aria-modal="true" aria-labelledby="tax-edit-title">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '12px',
                marginBottom: '14px',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <h2 id="tax-edit-title" style={TX.title}>
                  Edit taxonomy
                </h2>
                <div style={{ ...TX.hint, marginTop: '4px', fontSize: '12px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em', color: '#94a3b8' }}>TYPE</span>
                    <span
                      style={{
                        ...getTypeBadgeStyle(editItem.type),
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '3px 8px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: 600,
                      }}
                    >
                      {getTypeDisplayName(editItem.type)}
                    </span>
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '22px',
                  lineHeight: 1,
                  cursor: 'pointer',
                  color: '#94a3b8',
                  padding: '2px 4px',
                  borderRadius: '6px',
                  flexShrink: 0,
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div style={EM.section}>
              <label style={TX.label}>
                Title <span style={{ color: '#94a3b8' }}>*</span>
              </label>
              <input
                type="text"
                value={editItem.title || ''}
                onChange={(e) => setEditItem({ ...editItem, title: e.target.value })}
                style={TX.field}
              />
            </div>

            <div style={EM.section}>
              <label style={TX.label}>Short title</label>
              <input
                type="text"
                value={editItem.short_title != null ? editItem.short_title : ''}
                onChange={(e) => setEditItem({ ...editItem, short_title: e.target.value })}
                placeholder="e.g. AGR"
                style={TX.field}
              />
            </div>

            <div style={EM.section}>
              <label style={TX.label}>Description</label>
              <textarea
                value={editItem.description || ''}
                onChange={(e) => setEditItem({ ...editItem, description: e.target.value })}
                rows={3}
                style={{ ...TX.field, resize: 'vertical', minHeight: '72px' }}
              />
            </div>

            {usesPathwayScope(editItem.type) && (
              <div style={EM.section}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    flexWrap: 'wrap',
                    gap: '6px 12px',
                    marginBottom: '6px',
                  }}
                >
                  <span style={{ ...TX.label, display: 'inline', marginBottom: 0 }}>Pathway scope</span>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 500,
                      color: '#94a3b8',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {editPathwaySelectionCounts.cte} CTE, {editPathwaySelectionCounts.core} Core selected
                  </span>
                </div>
                <div
                  role="tablist"
                  aria-label="Pathway kind"
                  style={{
                    display: 'inline-flex',
                    padding: '3px',
                    borderRadius: '8px',
                    backgroundColor: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                    marginBottom: '8px',
                    gap: '2px',
                  }}
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={editPathwayScopeTab === 'cte'}
                    onClick={() => setEditPathwayScopeTab('cte')}
                    style={{
                      padding: '5px 14px',
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '0.03em',
                      borderRadius: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      ...(editPathwayScopeTab === 'cte'
                        ? {
                            backgroundColor: '#ffffff',
                            color: '#0369a1',
                            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
                          }
                        : { backgroundColor: 'transparent', color: '#64748b' }),
                    }}
                  >
                    CTE
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={editPathwayScopeTab === 'core'}
                    onClick={() => setEditPathwayScopeTab('core')}
                    style={{
                      padding: '5px 14px',
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '0.03em',
                      borderRadius: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      ...(editPathwayScopeTab === 'core'
                        ? {
                            backgroundColor: '#ffffff',
                            color: '#4338ca',
                            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
                          }
                        : { backgroundColor: 'transparent', color: '#64748b' }),
                    }}
                  >
                    Core
                  </button>
                </div>
                <div style={EM.scrollBox}>
                  {(editPathwayScopeTab === 'cte' ? pathwayRootsCte : pathwayRootsCore).length === 0 ? (
                    <div style={{ padding: '10px', fontSize: '12px', color: '#94a3b8', textAlign: 'center' }}>
                      {editPathwayScopeTab === 'cte' ? 'No CTE pathways' : 'No core subjects'}
                    </div>
                  ) : (
                    (editPathwayScopeTab === 'cte' ? pathwayRootsCte : pathwayRootsCore).map((root) => (
                      <label
                        key={root.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '4px',
                          fontSize: '12px',
                          color: '#334155',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={editItem.selectedConcentrations && editItem.selectedConcentrations.includes(root.id)}
                          onChange={(e) => {
                            const currentConcentrations = editItem.selectedConcentrations || [];
                            if (e.target.checked) {
                              setEditItem({ ...editItem, selectedConcentrations: [...currentConcentrations, root.id] });
                            } else {
                              setEditItem({
                                ...editItem,
                                selectedConcentrations: currentConcentrations.filter((id) => id !== root.id),
                              });
                            }
                          }}
                        />
                        <span>{root.title}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}

            {(editItem.type === 'Skill' || editItem.type === 'Objective') && (
              <div style={{ ...EM.section, marginBottom: '16px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    flexWrap: 'wrap',
                    gap: '6px 12px',
                    marginBottom: '6px',
                  }}
                >
                  <span style={{ ...TX.label, display: 'inline', marginBottom: 0 }}>Parents</span>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 500,
                      color: '#94a3b8',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {(editItem.selectedParents || []).length} Parent{(editItem.selectedParents || []).length === 1 ? '' : 's'} selected
                  </span>
                </div>
                {(() => {
                  const children = getChildren(editItem.id);
                  const isParent = children.length > 0;

                  if (isParent) {
                    return (
                      <div style={EM.warn}>
                        Has {children.length} child item{children.length !== 1 ? 's' : ''} — parent links stay fixed.
                      </div>
                    );
                  }

                  return (
                    <>
                      <input
                        type="text"
                        value={editParentSearchTerm}
                        onChange={(e) => setEditParentSearchTerm(e.target.value)}
                        placeholder="Search…"
                        style={{ ...TX.field, marginBottom: '8px' }}
                      />
                      <div style={EM.scrollBox}>
                        {getApplicableParents(editItem.type, editItem.id, editParentSearchTerm).map((p) => (
                          <label
                            key={p.id}
                            style={{
                              display: 'block',
                              marginBottom: '6px',
                              fontSize: '12px',
                              color: '#334155',
                              cursor: 'pointer',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={editItem.selectedParents && editItem.selectedParents.includes(p.id)}
                              onChange={(e) => {
                                const currentParents = editItem.selectedParents || [];
                                if (e.target.checked) {
                                  setEditItem({ ...editItem, selectedParents: [...currentParents, p.id] });
                                } else {
                                  setEditItem({
                                    ...editItem,
                                    selectedParents: currentParents.filter((id) => id !== p.id),
                                  });
                                }
                              }}
                              style={{ marginRight: '6px' }}
                            />
                            <span style={{ color: getTypeColor(p.type), fontWeight: 600, marginRight: '6px', fontSize: '11px' }}>
                              {getTypeDisplayName(p.type)}
                            </span>
                            {p.title || `Untitled (${p.id})`}
                          </label>
                        ))}
                        {getApplicableParents(editItem.type, editItem.id, editParentSearchTerm).length === 0 &&
                          editParentSearchTerm && (
                            <div style={{ padding: '8px', color: '#94a3b8', fontSize: '12px', textAlign: 'center' }}>
                              No match for “{editParentSearchTerm}”
                            </div>
                          )}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '6px' }}>
              <button type="button" onClick={() => setShowEditModal(false)} style={TX.btnSecondary}>
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={
                  !editItem.title ||
                  !String(editItem.title).trim() ||
                  ((editItem.type === 'Concentration' && !taxonomyCapabilities.EditConcentrations) ||
                    (editItem.type === 'Course-Subject' && !taxonomyCapabilities.EditCoreSubjects) ||
                    ((editItem.type === 'Standard' ||
                      editItem.type === 'Skill' ||
                      editItem.type === 'Objective') &&
                      !taxonomyCapabilities.EditStandardsSkillsObjectives))
                }
                style={{
                  ...TX.btnPrimary,
                  ...((!editItem.title || !String(editItem.title).trim()) ? TX.btnPrimaryDisabled : {}),
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create — pathway first (same order as TaxonomySelector / Assignment Builder context) */}
      {showCreateModal && (
        <div style={TX.overlay}>
          <div style={TX.panel} role="dialog" aria-modal="true" aria-labelledby="tax-create-title">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', gap: '12px' }}>
              <div>
                <h2 id="tax-create-title" style={TX.title}>
                  New taxonomy item
                </h2>
                <div style={{ ...TX.hint, marginTop: '4px' }}>
                  {newItem.type
                    ? `Step ${createStep} of ${getCreateMaxSteps(newItem.type)}`
                    : `Step ${createStep}`}
                </div>
              </div>
              <button
                type="button"
                onClick={closeCreateModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '22px',
                  lineHeight: 1,
                  cursor: 'pointer',
                  color: '#94a3b8',
                  padding: '2px 4px',
                  borderRadius: '6px',
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* 1 — Type first (pathway scope applies only to Standard / Skill / Objective) */}
            {createStep === 1 && (
              <div>
                <label style={TX.label}>
                  Type <span style={{ color: '#94a3b8' }}>*</span>
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {['Objective', 'Standard', 'Skill', 'Course-Subject', 'Concentration'].map((type) => {
                    const createAllowed =
                      (type === 'Concentration' && taxonomyCapabilities.CreateConcentration) ||
                      (type === 'Course-Subject' && taxonomyCapabilities.CreateCoreSubjects) ||
                      ((type === 'Standard' || type === 'Skill' || type === 'Objective') &&
                        taxonomyCapabilities.CreateStandardsSkillsObjectives);
                    return (
                      <button
                        key={type}
                        type="button"
                        disabled={!createAllowed}
                        onClick={() => {
                          if (!createAllowed) return;
                          setNewItem({
                            ...newItem,
                            type,
                            concentration: '',
                            parent: '',
                            short_title: '',
                          });
                          setCreatePathwayScopeTab('cte');
                          setCreateStep(2);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          textAlign: 'left',
                          padding: '9px 11px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          backgroundColor: createAllowed ? '#ffffff' : '#f8fafc',
                          cursor: createAllowed ? 'pointer' : 'not-allowed',
                          boxSizing: 'border-box',
                          font: 'inherit',
                          opacity: createAllowed ? 1 : 0.6,
                        }}
                      >
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                          {getTypeDisplayName(type)}
                        </span>
                        <span style={{ color: '#cbd5e1', fontSize: '16px', lineHeight: 1 }} aria-hidden>
                          ›
                        </span>
                      </button>
                    );
                  })}
                </div>
                {!canCreateAnyTaxonomy && (
                  <div style={{ ...TX.hint, marginTop: '10px', color: '#b91c1c' }}>
                    You do not currently have permission to create taxonomy items.
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
                  <button type="button" onClick={closeCreateModal} style={TX.btnSecondary}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* 2 — Pathway scope (Standard / Skill / Objective) OR title for Core subject only */}
            {createStep === 2 && usesPathwayScope(newItem.type) && (
              <div>
                <label style={TX.label}>Pathway or subject scope</label>
                <p style={{ ...TX.hint, marginTop: 0, marginBottom: '10px' }}>
                  Required. Tap a CTE pathway or core subject below to continue.
                </p>
                <div
                  role="tablist"
                  aria-label="Pathway kind"
                  style={{
                    display: 'inline-flex',
                    padding: '3px',
                    borderRadius: '8px',
                    backgroundColor: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                    marginBottom: '8px',
                    gap: '2px',
                  }}
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={createPathwayScopeTab === 'cte'}
                    onClick={() => setCreatePathwayScopeTab('cte')}
                    style={{
                      padding: '5px 14px',
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '0.03em',
                      borderRadius: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      ...(createPathwayScopeTab === 'cte'
                        ? {
                            backgroundColor: '#ffffff',
                            color: '#0369a1',
                            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
                          }
                        : { backgroundColor: 'transparent', color: '#64748b' }),
                    }}
                  >
                    CTE
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={createPathwayScopeTab === 'core'}
                    onClick={() => setCreatePathwayScopeTab('core')}
                    style={{
                      padding: '5px 14px',
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '0.03em',
                      borderRadius: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      ...(createPathwayScopeTab === 'core'
                        ? {
                            backgroundColor: '#ffffff',
                            color: '#4338ca',
                            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
                          }
                        : { backgroundColor: 'transparent', color: '#64748b' }),
                    }}
                  >
                    Core
                  </button>
                </div>
                <div
                  style={{
                    maxHeight: '220px',
                    overflowY: 'auto',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '6px',
                    background: '#fafafa',
                  }}
                >
                  {(createPathwayScopeTab === 'cte' ? pathwayRootsCte : pathwayRootsCore).length === 0 ? (
                    <div style={{ padding: '12px', fontSize: '12px', color: '#94a3b8', textAlign: 'center' }}>
                      {createPathwayScopeTab === 'cte' ? 'No CTE pathways' : 'No core subjects'}
                    </div>
                  ) : (
                    (createPathwayScopeTab === 'cte' ? pathwayRootsCte : pathwayRootsCore).map((root) => (
                      <button
                        key={root.id}
                        type="button"
                        onClick={() => {
                          setNewItem({ ...newItem, concentration: root.id });
                          setCreateStep(3);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          marginBottom: '6px',
                          padding: '10px 10px',
                          borderRadius: '8px',
                          border: `1px solid ${newItem.concentration === root.id ? '#94a3b8' : '#e2e8f0'}`,
                          backgroundColor: newItem.concentration === root.id ? '#f1f5f9' : '#ffffff',
                          cursor: 'pointer',
                          font: 'inherit',
                          fontSize: '13px',
                          color: '#334155',
                          textAlign: 'left',
                          boxSizing: 'border-box',
                        }}
                      >
                        <span>{root.title}</span>
                        <span style={{ color: '#94a3b8', flexShrink: 0 }} aria-hidden>
                          ›
                        </span>
                      </button>
                    ))
                  )}
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-start', marginTop: '16px' }}>
                  <button type="button" onClick={() => setCreateStep(1)} style={TX.btnSecondary}>
                    Back
                  </button>
                </div>
              </div>
            )}

            {createStep === 2 && newItem.type === 'Course-Subject' && (
              <div>
                <div
                  style={{
                    marginBottom: '14px',
                    padding: '10px 12px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #f1f5f9',
                  }}
                >
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px', fontWeight: 500 }}>Type</div>
                  <span
                    style={{
                      ...getTypeBadgeStyle(newItem.type),
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '5px 10px',
                      borderRadius: '8px',
                      fontSize: '13px',
                    }}
                  >
                    {getTypeDisplayName(newItem.type)}
                  </span>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={TX.label}>
                    Title <span style={{ color: '#94a3b8' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={newItem.title}
                    onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                    placeholder="Title"
                    style={TX.field}
                  />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={TX.label}>Description</label>
                  <textarea
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    placeholder="Optional"
                    rows={3}
                    style={{ ...TX.field, resize: 'vertical', minHeight: '72px' }}
                  />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={TX.label}>Abbreviation</label>
                  <input
                    type="text"
                    value={newItem.short_title}
                    onChange={(e) => setNewItem({ ...newItem, short_title: e.target.value })}
                    placeholder="Optional short code"
                    style={TX.field}
                  />
                </div>
                <div
                  style={{
                    marginBottom: '12px',
                    padding: '10px 12px',
                    backgroundColor: '#eef2ff',
                    borderRadius: '8px',
                    border: '1px solid #c7d2fe',
                    color: '#4338ca',
                    fontSize: '12px',
                    lineHeight: 1.45,
                  }}
                >
                  Core subjects are usually global in the catalog (Math, ELA, etc.) — not scoped to other pathway roots.
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '16px' }}>
                  <button type="button" onClick={() => setCreateStep(1)} style={TX.btnSecondary}>
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCreateItem()}
                    disabled={!newItem.title.trim()}
                    style={{
                      ...TX.btnPrimary,
                      ...(!newItem.title.trim() ? TX.btnPrimaryDisabled : {}),
                    }}
                  >
                    Create
                  </button>
                </div>
              </div>
            )}

            {createStep === 2 && newItem.type === 'Concentration' && (
              <div>
                <div
                  style={{
                    marginBottom: '14px',
                    padding: '10px 12px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #f1f5f9',
                  }}
                >
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px', fontWeight: 500 }}>Type</div>
                  <span
                    style={{
                      ...getTypeBadgeStyle(newItem.type),
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '5px 10px',
                      borderRadius: '8px',
                      fontSize: '13px',
                    }}
                  >
                    {getTypeDisplayName(newItem.type)}
                  </span>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={TX.label}>
                    Title <span style={{ color: '#94a3b8' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={newItem.title}
                    onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                    placeholder="Pathway name"
                    style={TX.field}
                  />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={TX.label}>Description</label>
                  <textarea
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    placeholder="Optional"
                    rows={3}
                    style={{ ...TX.field, resize: 'vertical', minHeight: '72px' }}
                  />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={TX.label}>Abbreviation</label>
                  <input
                    type="text"
                    value={newItem.short_title}
                    onChange={(e) => setNewItem({ ...newItem, short_title: e.target.value })}
                    placeholder="Optional short code (e.g. HOSP)"
                    style={TX.field}
                  />
                </div>
                <div
                  style={{
                    marginBottom: '12px',
                    padding: '10px 12px',
                    backgroundColor: '#e0f2fe',
                    borderRadius: '8px',
                    border: '1px solid #7dd3fc',
                    color: '#0369a1',
                    fontSize: '12px',
                    lineHeight: 1.45,
                  }}
                >
                  CTE pathways group industry-aligned courses and standards. They appear under the CTE tab when scoping items.
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '16px' }}>
                  <button type="button" onClick={() => setCreateStep(1)} style={TX.btnSecondary}>
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCreateItem()}
                    disabled={!newItem.title.trim()}
                    style={{
                      ...TX.btnPrimary,
                      ...(!newItem.title.trim() ? TX.btnPrimaryDisabled : {}),
                    }}
                  >
                    Create
                  </button>
                </div>
              </div>
            )}

            {/* 3 — Title & description (Standard / Skill / Objective only — after pathway) */}
            {createStep === 3 && usesPathwayScope(newItem.type) && (
              <div>
                <div
                  style={{
                    marginBottom: '14px',
                    padding: '10px 12px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #f1f5f9',
                  }}
                >
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px', fontWeight: 500 }}>Scope</div>
                  <div style={{ fontSize: '13px', color: '#334155' }}>{pathwayScopeLabel(newItem.concentration)}</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '10px', marginBottom: '6px', fontWeight: 500 }}>Type</div>
                  <span
                    style={{
                      ...getTypeBadgeStyle(newItem.type),
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '5px 10px',
                      borderRadius: '8px',
                      fontSize: '13px',
                    }}
                  >
                    {getTypeDisplayName(newItem.type)}
                  </span>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={TX.label}>
                    Title <span style={{ color: '#94a3b8' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={newItem.title}
                    onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                    placeholder="Title"
                    style={TX.field}
                  />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={TX.label}>Description</label>
                  <textarea
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    placeholder="Optional"
                    rows={3}
                    style={{ ...TX.field, resize: 'vertical', minHeight: '72px' }}
                  />
                </div>

                {newItem.type === 'Standard' && (
                  <div
                    style={{
                      marginBottom: '12px',
                      padding: '10px 12px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      color: '#475569',
                      fontSize: '12px',
                      lineHeight: 1.45,
                    }}
                  >
                    Standards are top-level for their scope — no parent step.
                  </div>
                )}

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '16px' }}>
                  <button type="button" onClick={() => setCreateStep(2)} style={TX.btnSecondary}>
                    Back
                  </button>
                  {newItem.type === 'Standard' ? (
                    <button
                      type="button"
                      onClick={() => handleCreateItem()}
                      disabled={!newItem.title.trim()}
                      style={{
                        ...TX.btnPrimary,
                        ...(!newItem.title.trim() ? TX.btnPrimaryDisabled : {}),
                      }}
                    >
                      Create
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setCreateStep(4)}
                      disabled={!newItem.title.trim()}
                      style={{
                        ...TX.btnPrimary,
                        ...(!newItem.title.trim() ? TX.btnPrimaryDisabled : {}),
                      }}
                    >
                      Next
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* 4 — Parent (Skill / Objective) */}
            {createStep === 4 && (newItem.type === 'Skill' || newItem.type === 'Objective') && (
              <div>
                <div
                  style={{
                    marginBottom: '12px',
                    padding: '10px 12px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #f1f5f9',
                    fontSize: '13px',
                    color: '#475569',
                    lineHeight: 1.5,
                  }}
                >
                  <div>
                    <span style={{ color: '#64748b', fontWeight: 500 }}>Type</span> {getTypeDisplayName(newItem.type)}
                  </div>
                  <div style={{ marginTop: '4px' }}>
                    <span style={{ color: '#64748b', fontWeight: 500 }}>Title</span> {newItem.title}
                  </div>
                  <div style={{ marginTop: '4px' }}>
                    <span style={{ color: '#64748b', fontWeight: 500 }}>Scope</span> {pathwayScopeLabel(newItem.concentration)}
                  </div>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={TX.label}>Parent (optional)</label>
                  <input
                    type="text"
                    value={parentSearchTerm}
                    onChange={(e) => setParentSearchTerm(e.target.value)}
                    placeholder="Search parents…"
                    style={{ ...TX.field, marginBottom: '10px' }}
                  />
                  <div
                    style={{
                      maxHeight: '240px',
                      overflowY: 'auto',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '6px',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => handleCreateItem({ parent: '' })}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        marginBottom: '8px',
                        padding: '10px 12px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        cursor: 'pointer',
                        font: 'inherit',
                        textAlign: 'left',
                        boxSizing: 'border-box',
                      }}
                    >
                      <span style={{ color: '#64748b', fontSize: '13px', fontWeight: 500 }}>No parent</span>
                      <span style={{ color: '#94a3b8' }} aria-hidden>
                        ›
                      </span>
                    </button>
                    {getApplicableParents(newItem.type, null, parentSearchTerm).map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => handleCreateItem({ parent: p.id })}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          marginBottom: '8px',
                          padding: '10px 12px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                          cursor: 'pointer',
                          font: 'inherit',
                          textAlign: 'left',
                          boxSizing: 'border-box',
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', minWidth: 0 }}>
                          <span style={{ ...getTypeBadgeStyle(p.type), fontSize: '11px', padding: '2px 6px' }}>
                            {getTypeDisplayName(p.type)}
                          </span>
                          <span style={{ fontWeight: 600, fontSize: '13px', color: '#334155' }}>{p.title || p.id}</span>
                        </span>
                        <span style={{ color: '#94a3b8', flexShrink: 0 }} aria-hidden>
                          ›
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-start', marginTop: '16px' }}>
                  <button type="button" onClick={() => setCreateStep(3)} style={TX.btnSecondary}>
                    Back
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Taxonomy; 